import { AnyFormApi } from '@tanstack/react-form';
import { NextResponse } from 'next/server';
import { z, ZodIssue, ZodRawShape, ZodTypeAny } from 'zod';

type Value = string | number | boolean;
type JsonData = { [key: string]: Value | Value[] | JsonData };
export type ZodObjectShape<R extends JsonData> = {
  [k in keyof R]?: ZodTypeAny;
};

export type StatefulFormResponsePayload<D = {}> =
  | ({ success: true } & D)
  | ValidationResponsePayload;

export type ValidationErrorResponse = NextResponse<ValidationResponsePayload>;
export type ValidationResponsePayload = {
  success: false;
  message: string;
  fieldErrors?: ZodIssue[];
  formErrors?: string[];
};
type ValidationResponse<Data> =
  | { passes: true; data: Data }
  | {
      passes: false;
      validationResponse: ValidationResponsePayload;
      errorNextResponse: ValidationErrorResponse;
    };

export async function validateFormData<Data extends JsonData>(
  data: Data,
  validation: ZodObjectShape<Data>,
): Promise<ValidationResponse<Data>> {
  const zodValidated = await z
    .object(validation as ZodRawShape)
    .safeParseAsync(data);

  if (zodValidated.success) {
    return { passes: true, data: { ...data, ...zodValidated.data } };
  } else {
    const payload: ValidationResponsePayload = {
      success: false,
      message: 'validation failed',
      fieldErrors: zodValidated.error.errors,
    };

    return {
      passes: false,
      validationResponse: payload,
      errorNextResponse: NextResponse.json(payload),
    };
  }
}

export function mapErrorResponseIntoForm(
  response: ValidationResponsePayload | undefined,
  formApi: AnyFormApi,
): void {
  if (!response) return;
  response.fieldErrors?.forEach(error => {
    if (error.path.length !== 1) {
      return; // Not supported yet
    }

    const field = String(error.path[0]);

    formApi.setFieldMeta(field, prev => ({
      ...prev,
      errorMap: {
        onServer: { message: error.message },
      },
      errors: [{ message: error.message }],
    }));
  });

  if (response.formErrors?.length) {
    const errors = response.formErrors?.map(e => ({ message: e })) ?? [];
    formApi.baseStore.setState(prev => ({
      ...prev,
      errors,
      errorMap: {
        ...prev.errorMap,
        onServer: errors.map(({ message }) => message).join(', '),
      },
    }));
  }
}
